import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import Draggable from 'react-draggable';
import ColorHash from 'color-hash';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import RootRef from '@material-ui/core/RootRef';

import { Scrollbars } from 'react-custom-scrollbars';

import Button from 'components/common/Button';
import Tooltip from 'components/common/Tooltip';
import PrintDialog from 'components/common/PrintDialog';
import DocumentsSignDialog from 'components/common/DocumentsSignDialog';
import Menu, { MenuItem } from 'components/common/Menu';
import IconButton from 'components/common/IconButton';
import IconMoreVert from 'components/icons/IconMoreVert';
import IconBlurb from 'components/icons/IconBlurb';
import Link from 'components/common/Link';
import IconHome from 'components/icons/IconHome';
import IconCheck from 'components/icons/IconCheck';
import OrderNotes from 'components/workflow/OrderNotes';

import formatUtcToLocalDate from 'utils/formatUtcToLocalDate';
import isDescendant from 'utils/isDescendant';
import getDocumentFieldNames from 'utils/getDocumentFieldNames';
import getCurrentAppDocuments from 'utils/getCurrentAppDocuments';
import getPatientFullAddress from 'utils/print/getPatientFullAddress';

import timeDateFormat from 'config/timeDateFormat';
import documents from 'config/documents';
import hexToRgb from 'styles/hexToRgb';
import pxToEm from 'styles/pxToEm';

import { fetchOrder } from 'redux/modules/orders';

import ScheduleCalendarItemOrderLine from './ScheduleCalendarItemOrderLine';
import ScheduleCalendarItemStatusForm from './ScheduleCalendarItemStatusForm';

const hoverShift = 16;

const styles = theme => ({
  root: {
    position: 'relative',
    minHeight: 54,
    '& + $root': {
      marginTop: 3,
    },
  },
  rootCollapsed: {
    height: 24,
  },
  rootDragged: {
    zIndex: 20,
  },
  rootHovered: {
    zIndex: 10,
  },
  rootHidden: {
    display: 'none',
  },
  paper: {
    cursor: 'pointer',
    position: 'absolute',
    top: 0,
    userSelect: 'none',
    transition: theme.transitions.create(['height', 'transform'], {
      duration: theme.transitions.duration.shortest,
    }),
  },
  paperVariantDateRange: {
    width: 494,
  },
  paperVariantSingleDate: {
    width: 494,
  },
  paperHovered: {
    transform: `translateX(${hoverShift}px)`,
  },
  paperDisabled: {
    backgroundColor: theme.palette.grey[100],
  },
  paperNotAllowed: {
    cursor: 'not-allowed',
  },
  content: {
    position: 'relative',
    boxSizing: 'border-box',
    padding: '3px 155px 3px 12px',
    borderRadius: 4,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'transparent',
    minHeight: 54,
    zIndex: 2,
    backgroundColor: theme.palette.common.white,
  },
  contentCollapsed: {
    height: 24,
    '& $buttonMoreMenu': {
      opacity: 0,
    },
    '& $contentSecondary': {
      opacity: 0,
    },
    '& $tooltipNotes': {
      opacity: 0,
    },
  },
  buttonMoreMenu: {
    width: 32,
    height: 32,
    position: 'absolute',
    right: 0,
    top: 8,
    transition: theme.transitions.create(['opacity'], {
      duration: theme.transitions.duration.shortest,
    }),
  },
  contentPrimary: {
    display: 'flex',
  },
  gridItemPatientName: {
    maxWidth: '100%',
  },
  gridItemPatientNameNoAddress: {
    maxWidth: '85%',
  },
  typographyPatientName: {
    maxWidth: 240,
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: pxToEm(15, 14),
  },
  typographyAddress: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: pxToEm(15, 14),
  },
  contentSecondary: {
    marginTop: 8,
    marginBottom: 8,
    transition: theme.transitions.create(['opacity'], {
      duration: theme.transitions.duration.shortest,
    }),
  },
  gridItemDriverName: {
    maxWidth: 120,
  },
  typographySecondary: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  iconMoreVert: {
    width: 24,
    height: 24,
  },
  colorHash: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: 6,
    zIndex: 2,
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  orderLines: {
    backgroundColor: theme.palette.grey[100],
    marginRight: -155,
    marginBottom: -3,
    marginLeft: -12,
    marginTop: 4,
    position: 'relative',
    borderBottomRightRadius: 4,
    borderBottomLeftRadius: 4,
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 1,
      backgroundColor: theme.palette.primary.A100,
    },
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: -hoverShift,
      width: hoverShift,
    },
  },
  scrollbarsOrderLineDragged: {
    overflow: 'visible!important',
    '& > div': {
      overflow: 'visible!important',
      marginRight: '0!important',
      marginBottom: '0!important',
    },
  },
  trackVertical: {
    zIndex: 1,
  },
  trackHorizontal: {
    zIndex: 1,
  },
  collapseEntered: {
    overflow: 'visible',
  },
  buttonEditPatientAddress: {
    width: 32,
    height: 32,
    margin: '-8px -6px',
    color: theme.palette.validation[500],
  },
  iconHome: {
    width: 20,
    height: 17,
  },
  iconCheck: {
    width: 16,
    height: 16,
    color: theme.palette.add[600],
  },
  iconBlurb: {
    width: 18,
    height: 17,
  },
  tooltipNotes: {
    position: 'absolute',
    right: 38,
    top: 8,
    transition: theme.transitions.create(['opacity'], {
      duration: theme.transitions.duration.shortest,
    }),
  },
  buttonNotes: {
    width: 32,
    height: 32,
  },
  typographyNotes: {
    position: 'absolute',
    left: 28,
    top: '50%',
    marginTop: -8,
    color: theme.palette.primary[500],
  },
  buttonNotesWithNotes: {
    color: theme.palette.primary[500],
  },
  buttonNotesActive: {
    backgroundColor: `rgba(${hexToRgb(theme.palette.common.black)}, 0.08)`,
  },
  driverName: {
    display: 'inline-block',
    color: theme.palette.text.primary,
  },
  buttonSign: {
    position: 'absolute',
    top: 8,
    right: 80,
  },
});

const colorHash = new ColorHash();

class ScheduleCalendarItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dragged: false,
      orderLineDragged: false,
      orderLinesOpen: false,
      orderLinesAutoHeightMax: 200,
      hovered: false,
      position: null,
      bounds: null,
      width: 0,
      height: 0,
      orderLinesScrollTop: 0,
      orderLinesStyles: null,
      anchorEl: null,
      printDialogOpen: false,
      signDialogOpen: false,
      printFormValues: null,
      notesTooltipHovered: false,
      notesTooltipClicked: false,
    };

    this.onDraggableDataChange = this.onDraggableDataChange.bind(this);
    this.onStartDrag = this.onStartDrag.bind(this);
    this.onDrag = this.onDrag.bind(this);
    this.onStopDrag = this.onStopDrag.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onOrderLineStartDrag = this.onOrderLineStartDrag.bind(this);
    this.onOrderLineDrag = this.onOrderLineDrag.bind(this);
    this.onOrderLineStopDrag = this.onOrderLineStopDrag.bind(this);
    this.onOrderLinesLinkClick = this.onOrderLinesLinkClick.bind(this);
    this.onOrderLinesClickAway = this.onOrderLinesClickAway.bind(this);
    this.onScrollbarsScrollFrame = this.onScrollbarsScrollFrame.bind(this);
    this.renderTrackHorizontal = this.renderTrackHorizontal.bind(this);
    this.renderTrackVertical = this.renderTrackVertical.bind(this);
    this.onMoreMenuButtonClick = this.onMoreMenuButtonClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onPrintMenuItemClick = this.onPrintMenuItemClick.bind(this);
    this.onPrintDialogClose = this.onPrintDialogClose.bind(this);
    this.onPrintDialogSign = this.onPrintDialogSign.bind(this);
    this.onSignDialogClose = this.onSignDialogClose.bind(this);
    this.onNotesButtonMouseEnter = this.onNotesButtonMouseEnter.bind(this);
    this.onNotesTooltipMouseEnter = this.onNotesTooltipMouseEnter.bind(this);
    this.onNotesTooltipMouseLeave = this.onNotesTooltipMouseLeave.bind(this);
    this.onNotesButtonMouseLeave = this.onNotesButtonMouseLeave.bind(this);
    this.onOrderNotesSubmitSuccess = this.onOrderNotesSubmitSuccess.bind(this);
    this.onNotesButtonFocus = this.onNotesButtonFocus.bind(this);
    this.onOrderNotesSubmitButtonBlur = this.onOrderNotesSubmitButtonBlur.bind(this);
    this.onNotesButtonClick = this.onNotesButtonClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onSignButtonClick = this.onSignButtonClick.bind(this);
  }

  componentDidMount() {
    this.onDraggableDataChange();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.draggable && !this.props.draggable) {
      this.onDraggableDataChange();
    }
  }

  onDraggableDataChange() {
    const { boundsSelector } = this.props;
    if (boundsSelector && this.draggableRef) {
      const draggableRefDOMRect = this.draggableRef.getBoundingClientRect();
      const boundsDOMRect = document.querySelector(boundsSelector).getBoundingClientRect();
      const left = boundsDOMRect.left - draggableRefDOMRect.left;
      const top = boundsDOMRect.top - draggableRefDOMRect.top;
      const right = boundsDOMRect.width - draggableRefDOMRect.width - left;
      const bottom = (boundsDOMRect.height - draggableRefDOMRect.height) + top;
      this.setState({
        bounds: { left, top, right, bottom },
        width: draggableRefDOMRect.width,
        height: draggableRefDOMRect.height,
      });
    }
  }

  onStartDrag(event, data) {
    const { onStartDrag, onOrderLinesToggle } = this.props;
    const { bounds, width, height } = this.state;
    onOrderLinesToggle({
      orderLinesOpen: false,
    });
    this.onDraggableDataChange();
    this.setState({
      dragged: true,
      orderLinesOpen: false,
    });
    if (onStartDrag) {
      onStartDrag({
        x: (data.x - bounds.left) + hoverShift,
        y: data.y - bounds.top,
        width,
        height,
        defaultX: -bounds.left,
        defaultY: -bounds.top,
      });
    }
  }

  onDrag(event, data) {
    const { onDrag } = this.props;
    const { bounds, width, height } = this.state;
    if (onDrag) {
      onDrag({
        x: (data.x - bounds.left) + hoverShift,
        y: data.y - bounds.top,
        width,
        height,
        defaultX: -bounds.left,
        defaultY: -bounds.top,
      });
    }
  }

  onStopDrag(event, data) {
    const { onStopDrag, ID, Patient, ...otherProps } = this.props;
    const { bounds, width, height } = this.state;
    this.setState({
      dragged: false,
      hovered: false,
      position: {
        x: 0,
        y: 0,
      },
    });
    if (onStopDrag) {
      onStopDrag({
        x: (data.x - bounds.left) + hoverShift,
        y: data.y - bounds.top,
        width,
        height,
        defaultX: -bounds.left,
        defaultY: -bounds.top,
        item: {
          ID,
          Patient,
          'Order Line': otherProps['Order Line'],
        },
      });
    }
  }

  onMouseEnter() {
    this.setState({ hovered: true });
  }

  onMouseLeave() {
    this.setState({ hovered: false });
  }

  onOrderLineStartDrag(data) {
    const { orderLinesAutoHeightMax, orderLinesScrollTop } = this.state;
    const draggedOrderLineIndex = this.props['Order Line']
      .findIndex(item => item.ID === data.ID);
    const orderLineHeight = 64;
    let offsetTop = orderLineHeight;
    let offsetInView = 0;
    const orderLinesStyles = this.props['Order Line'].map((item, index) => {
      let height;
      let alignItems;
      if (offsetTop < orderLinesScrollTop) {
        height = '0px';
      } else if (offsetTop > (orderLinesScrollTop + orderLinesAutoHeightMax)) {
        if ((offsetTop - orderLineHeight) > (orderLinesScrollTop + orderLinesAutoHeightMax)) {
          height = '0px';
        } else {
          height = `${orderLinesAutoHeightMax - offsetInView}px`;
        }
      } else {
        const currentItemHeight = offsetTop - orderLinesScrollTop - offsetInView;
        height = `${currentItemHeight}px`;
        alignItems = 'flex-end';
        offsetInView += currentItemHeight;
      }
      offsetTop += orderLineHeight;
      return {
        height,
        ...index !== draggedOrderLineIndex ? { overflow: 'hidden' } : {},
        ...alignItems ? { alignItems } : {},
      };
    });
    this.setState({
      orderLineDragged: true,
      orderLinesStyles,
    });
  }

  onOrderLineDrag(data) {
    this.props.onDrag(data);
  }

  onOrderLineStopDrag({ ID, ...otherData }) {
    const { onStopDrag, Patient, ...otherProps } = this.props;
    this.setState({
      orderLineDragged: false,
      orderLinesStyles: null,
    });
    if (onStopDrag) {
      onStopDrag({
        ...otherData,
        item: {
          ID: ID.toString(),
          Patient,
          'Order Line': otherProps['Order Line'].filter(orderLine => orderLine.ID === ID),
        },
      });
    }
  }

  onOrderLinesLinkClick() {
    const { onOrderLinesToggle } = this.props;
    const orderLinesOpen = !this.state.orderLinesOpen;
    this.setState({ orderLinesOpen });
    onOrderLinesToggle({
      orderLinesOpen,
      orderLinesLength: this.props['Order Line'].length,
      orderLinesOffsetBottom: this.orderLinesLinkRef.getBoundingClientRect().bottom,
    });
  }

  onOrderLinesClickAway(event) {
    const { onOrderLinesToggle } = this.props;
    if (this.orderLinesLinkRef !== event.target
      && !isDescendant(this.orderLinesLinkRef, event.target)) {
      this.setState({ orderLinesOpen: false });
      onOrderLinesToggle({
        orderLinesOpen: false,
      });
    }
  }

  onScrollbarsScrollFrame({ scrollTop }) {
    this.setState({ orderLinesScrollTop: scrollTop });
  }

  onMoreMenuButtonClick(event) {
    this.setState({ anchorEl: event.currentTarget });
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onPrintMenuItemClick() {
    this.setState({ printDialogOpen: true });
    this.props.actions.fetchOrder({
      ID: this.props['Order Line'][0]['Order ID'],
      triggerLoading: false,
    });
  }

  onPrintDialogClose() {
    this.setState({ printDialogOpen: false });
  }

  onPrintDialogSign(values) {
    this.setState({
      printDialogOpen: false,
      printFormValues: values,
      signDialogOpen: true,
    });
  }

  onSignDialogClose() {
    this.setState({ signDialogOpen: false });
    this.props.onEnableDrag();
  }

  onNotesButtonMouseEnter() {
    this.setState({ notesTooltipHovered: true });
    if (this.notesTooltipCloseTimeout) {
      clearTimeout(this.notesTooltipCloseTimeout);
    }
    setTimeout(() => {
      if (this.createOrderNoteInputRef) {
        this.createOrderNoteInputRef.focus();
      }
    }, 100);
  }

  onNotesTooltipMouseEnter() {
    this.setState({ notesTooltipHovered: true });
    if (this.notesTooltipCloseTimeout) {
      clearTimeout(this.notesTooltipCloseTimeout);
    }
    setTimeout(() => {
      if (this.createOrderNoteInputRef) {
        this.createOrderNoteInputRef.focus();
      }
    }, 100);
  }

  onNotesButtonFocus() {
    this.setState({ notesTooltipHovered: true });
    if (this.buttonBlurTimeout) {
      clearTimeout(this.buttonBlurTimeout);
    }
    if (this.notesTooltipCloseTimeout) {
      clearTimeout(this.notesTooltipCloseTimeout);
    }
    setTimeout(() => {
      if (this.createOrderNoteInputRef) {
        this.createOrderNoteInputRef.focus();
      }
    }, 100);
  }

  onNotesTooltipMouseLeave() {
    this.setState({ notesTooltipHovered: false });
  }

  onNotesButtonMouseLeave() {
    this.setState({ notesTooltipHovered: false });
  }

  onOrderNotesSubmitSuccess(values, dispatch, props) {
    this.setState({ notesTooltipHovered: false });
    if (props && props.reset) {
      props.reset();
    }
  }

  onOrderNotesSubmitButtonBlur() {
    this.setState({ notesTooltipHovered: false });
    const menuButtonEl = this.tableRowRef.querySelector(`#menu-button-${this.props.ID}`);
    if (menuButtonEl) {
      menuButtonEl.focus();
    }
  }

  renderTrackHorizontal({ style, ...props }) {
    const { classes } = this.props;
    const finalStyle = {
      ...style,
      right: 2,
      bottom: 2,
      left: 2,
      borderRadius: 3,
    };
    return (
      <div
        className={classes.trackHorizontal}
        style={finalStyle}
        {...props}
      />
    );
  }

  renderTrackVertical({ style, ...props }) {
    const { classes } = this.props;
    const finalStyle = {
      ...style,
      right: 2,
      bottom: 2,
      top: 2,
      borderRadius: 3,
    };
    return (
      <div
        className={classes.trackVertical}
        style={finalStyle}
        {...props}
      />
    );
  }

  onNotesButtonClick() {
    if (!this.state.notesTooltipClicked) {
      document.addEventListener('click', this.onDocumentClick);
    } else {
      document.removeEventListener('click', this.onDocumentClick);
    }
    this.setState({ notesTooltipClicked: !this.state.notesTooltipClicked });
  }

  onDocumentClick(event) {
    if (!isDescendant(this.orderNotesRef, event.target)) {
      document.removeEventListener('click', this.onDocumentClick);
      this.setState({ notesTooltipClicked: false });
    }
  }

  onSignButtonClick() {
    this.props.onDisableDrag();
    this.props.onSign({
      'Record Number': this.props.Patient['Record Number'],
      'Date of Delivery': this.props['Order Line'][0]['Date of Delivery'],
      'Driver ID': this.props['Order Line'][0].Driver.ID,
    });
  }

  render() {
    const {
      Patient,
      Prescriber,
      Facility,
      className,
      collapsed,
      variant,
      draggable,
      loading,
      disabled,
      hidden,
      boundsSelector,
      history,
      business,
      Insurance,
      account,
      notAllowed,
      classes,
      ...otherProps
    } = this.props;
    const {
      dragged,
      orderLineDragged,
      hovered,
      position,
      bounds,
      orderLinesOpen,
      orderLinesAutoHeightMax,
      orderLinesStyles,
      anchorEl,
      printDialogOpen,
      signDialogOpen,
      printFormValues,
      notesTooltipClicked,
      notesTooltipHovered,
    } = this.state;
    const notesTooltipOpen = notesTooltipClicked ? true : notesTooltipHovered;
    const driver = otherProps['Order Line'][0].Driver;
    const delivered = otherProps['Order Line'][0]['Is Delivered'];
    const card = (
      <Paper
        className={classNames(classes.paper, {
          [classes.paperVariantSingleDate]: variant === 'single-date',
          [classes.paperVariantDateRange]: variant === 'date-range',
          [classes.paperHovered]: hovered || orderLinesOpen,
          [classes.paperDisabled]: delivered,
          [classes.paperNotAllowed]: notAllowed,
        })}
      >
        <div
          className={classNames(classes.content, {
            // [classes.contentCollapsed]: collapsed && !hovered && !dragged,
          })}
          {...driver.ID ? {
            style: {
              borderColor: colorHash.hex(`00000${driver.ID}`.slice(-6)),
            },
          } : {}}
        >
          <div className={classes.contentPrimary}>
            <Grid
              className={classes.grid}
              container
              justify="flex-start"
              alignItems="center"
              wrap="nowrap"
              spacing={8}
            >
              {!Patient['Exists Address'] &&
                <Grid item>
                  <IconButton
                    className={classes.buttonEditPatientAddress}
                    href={`/charts/${Patient['Record Number']}#contact-information`}
                    onMouseDown={(event) => {
                      event.stopPropagation();
                    }}
                    tooltipContent="No Address Information"
                  >
                    <IconHome className={classes.iconHome} />
                  </IconButton>
                </Grid>
              }
              {delivered &&
                <Grid item>
                  <Tooltip
                    content={`Delivered at ${formatUtcToLocalDate(
                      otherProps['Order Line'][0]['Date of Delivered'],
                      timeDateFormat,
                      timeDateFormat,
                      business['Company Address']['Time Zone'],
                      business['Company Address']['Observes Daylight Saving'],
                    )}`}
                    placement="top"
                    color="dark"
                  >
                    <IconCheck className={classes.iconCheck} />
                  </Tooltip>
                </Grid>
              }
              <Grid
                className={classNames(classes.gridItemPatientName, {
                  [classes.gridItemPatientNameNoAddress]: !Patient['Exists Address'],
                })}
                item
              > 
                <Grid
                  container
                  spacing={8}
                  alignItems="center"
                  justify="flex-start"
                  wrap="nowrap"
                >
                  <Grid item>
                    <Typography
                      className={classes.typographyPatientName}
                    >
                      {`${Patient['First Name']}, ${Patient['Last Name']}`}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography>
                      |
                    </Typography>
                  </Grid>
                  <Grid item>
                    <RootRef
                      rootRef={(orderLinesLinkRef) => {
                        this.orderLinesLinkRef = orderLinesLinkRef;
                      }}
                    >
                      <Link
                        {...draggable && !disabled ? {
                          onClick: () => {
                            this.onOrderLinesLinkClick();
                          },
                        } : {}}
                        color="secondary"
                        size="sm"
                        underline
                        disabled={!draggable}
                        onMouseDown={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        {`${otherProps['Order Line'].length} items`}
                      </Link>
                    </RootRef>
                  </Grid>
                </Grid>
                {Patient && Patient['Exists Address'] && (
                  <Typography
                    className={classes.typographyAddress}
                  >
                    {getPatientFullAddress({ patient: Patient })}
                  </Typography>
                )}
                {variant === 'date-range' &&
                  <Typography
                    className={classes.typographySecondary}
                    variant="caption"
                  >
                    {driver.ID && (
                      <Fragment>
                        Driver: <div className={classes.driverName}>{`${driver['First Name']} ${driver['Last Name']}`}</div>
                      </Fragment>
                    )}
                    {!driver.ID &&
                      'Driver not assigned'}
                  </Typography>
                }
              </Grid>
            </Grid>
          </div>
          {(draggable && !disabled) &&
            <ClickAwayListener
              onClickAway={this.onOrderLinesClickAway}
            >
              {orderLinesOpen &&
                <div className={classNames(classes.orderLines)}>
                  <Scrollbars
                    className={classNames({
                      [classes.scrollbarsOrderLineDragged]: orderLineDragged,
                    })}
                    autoHeight
                    autoHeightMin={0}
                    autoHeightMax={orderLinesAutoHeightMax}
                    thumbMinSize={30}
                    universal
                    onScrollFrame={this.onScrollbarsScrollFrame}
                    {...orderLineDragged ? {
                      renderTrackHorizontal: () => <span />,
                      renderTrackVertical: () => <span />,
                    } : {
                      renderTrackHorizontal: this.renderTrackHorizontal,
                      renderTrackVertical: this.renderTrackVertical,
                    }}
                  >
                    {otherProps['Order Line'].map((orderLine, index) => (
                      <ScheduleCalendarItemOrderLine
                        key={orderLine.ID}
                        {...orderLine}
                        boundsSelector={boundsSelector}
                        onStartDrag={this.onOrderLineStartDrag}
                        onDrag={this.onOrderLineDrag}
                        onStopDrag={this.onOrderLineStopDrag}
                        disabled={dragged || otherProps['Order Line'].length === 1 || delivered}
                        {...orderLinesStyles ? {
                          style: orderLinesStyles[index],
                        } : {}}
                      />
                    ))}
                  </Scrollbars>
                </div>
              }
            </ClickAwayListener>
          }
          {draggable &&
            <Fragment>
              <Button
                className={classes.buttonSign}
                onClick={this.onSignButtonClick}
                variant="raised"
                color="primary"
                size="sm"
              >
                Sign
              </Button>
              <Tooltip
                className={classes.tooltipNotes}
                popperClassName={classes.noteTooltipPopper}
                content={
                  <OrderNotes
                    {...{ 'Author ID': account.ID }}
                    {...{ 'Order ID': otherProps['Order Line'][0]['Order ID'] }}
                    {...{ 'Order Notes': otherProps.Notes }}
                    onSubmitSuccess={this.onOrderNotesSubmitSuccess}
                    onSubmitButtonBlur={this.onOrderNotesSubmitButtonBlur}
                    rootRef={(orderNotesRef) => {
                      this.orderNotesRef = orderNotesRef;
                    }}
                    createInputRef={(createOrderNoteInputRef) => {
                      this.createOrderNoteInputRef = createOrderNoteInputRef;
                    }}
                    business={business}
                  />
                }
                disabled={!hovered && !notesTooltipClicked}
                disableHoverListener
                open={notesTooltipOpen}
                disablePointerEvents={false}
                onMouseLeave={this.onNotesTooltipMouseLeave}
                onMouseEnter={this.onNotesTooltipMouseEnter}
              >
                <IconButton
                  className={classNames(classes.buttonNotes, {
                    [classes.buttonNotesTooltipOpen]: notesTooltipOpen,
                    [classes.buttonNotesWithNotes]: otherProps.Notes.length > 0,
                    [classes.buttonNotesActive]: notesTooltipClicked,
                  })}
                  onMouseOver={this.onNotesButtonMouseEnter}
                  onMouseLeave={this.onNotesButtonMouseLeave}
                  onFocus={this.onNotesButtonFocus}
                  onBlur={this.onNotesButtonBlur}
                  onClick={this.onNotesButtonClick}
                >
                  <IconBlurb className={classes.iconBlurb} />
                  {(otherProps.Notes && otherProps.Notes.length > 0) &&
                    <Typography
                      className={classes.typographyNotes}
                      variant="caption"
                    >
                      {otherProps.Notes.length}
                    </Typography>
                  }
                </IconButton>
              </Tooltip>
              <IconButton
                className={classes.buttonMoreMenu}
                aria-label="More"
                aria-owns={anchorEl
                  ? `${otherProps['Order Line'][0]}-schedule-delivery-more-menu` : null}
                aria-haspopup="true"
                tooltipContent="Open Menu"
                {...!disabled ? {
                  onClick: (event) => {
                    this.onMoreMenuButtonClick(event);
                  },
                } : {}}
              >
                <IconMoreVert className={classes.iconMoreVert} />
              </IconButton>
              <Menu
                id={`${otherProps['Order Line'][0]}-schedule-delivery-more-menu`}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={this.onMenuClose}
                list
              >
                <MenuItem
                  onClick={() => {
                    window.open(`/charts/${Patient['Record Number']}`, '_blank');
                    this.onMenuClose();
                  }}
                >
                  View Patient
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    window.open(`/workflow/order/view/${otherProps['Order Line'][0]['Order ID']}`, '_blank');
                    this.onMenuClose();
                  }}
                >
                  View Order
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    window.open(`/workflow/order/edit/${otherProps['Order Line'][0]['Order ID']}`, '_blank');
                    this.onMenuClose();
                  }}
                >
                  Edit Order
                </MenuItem>
                <MenuItem onClick={this.onPrintMenuItemClick}>
                  Print Data
                </MenuItem>
              </Menu>
              <PrintDialog
                form={`${otherProps['Order Line'].map(item => item.ID).join(',')}-orderLinesPrint`}
                variant="order"
                open={printDialogOpen}
                onClose={this.onPrintDialogClose}
                onSign={this.onPrintDialogSign}
                ID={otherProps['Order Line'][0]['Order ID']}
                patient={Patient}
                prescriber={Prescriber}
                {...{
                  'Order Line': otherProps['Order Line'],
                  'Primary Insurance': otherProps['Primary Insurance'],
                  'Order Insurance Param': otherProps['Order Insurance Param'],
                }}
              />
              <DocumentsSignDialog
                open={signDialogOpen}
                onClose={this.onSignDialogClose}
                printFormValues={printFormValues}
                variant="order"
                ID={otherProps['Order Line'][0]['Order ID']}
                patient={Patient}
                prescriber={Prescriber}
                facility={Facility}
                {...{
                  'Order Line': otherProps['Order Line'],
                  'Primary Insurance': otherProps['Primary Insurance'],
                  'Order Insurance Param': otherProps['Order Insurance Param'],
                }}
              />
            </Fragment>
          }
        </div>
        {variant === 'date-range' && draggable &&
            <div
              className={classes.colorHash}
              {...driver.ID ? {
                style: {
                  backgroundColor: colorHash.hex(`00000${driver.ID}`.slice(-6)),
                },
              } : {}}
            />
          }
      </Paper>
    );
    return (
      <RootRef
        rootRef={(cardRef) => {
          this.cardRef = cardRef;
        }}
      >
        <div
          className={classNames(classes.root, className, {
            // [classes.rootCollapsed]: collapsed,
            [classes.rootDragged]: dragged || orderLineDragged,
            [classes.rootHovered]: hovered || orderLinesOpen,
            [classes.rootHidden]: hidden,
          })}
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseLeave}
        >
          {draggable ? (
            <Draggable
              {...bounds ? { bounds } : {}}
              {...position ? { position } : {}}
              disabled={loading || delivered || notesTooltipOpen || disabled}
              onStart={this.onStartDrag}
              onDrag={this.onDrag}
              onStop={this.onStopDrag}
              enableUserSelectHack={false}
            >
              <div
                ref={(draggableRef) => {
                  this.draggableRef = draggableRef;
                }}
              >
                {card}
              </div>
            </Draggable>
          ) : (
            card
          )}
        </div>
      </RootRef>
    );
  }
}

ScheduleCalendarItem.propTypes = {
  ID: PropTypes.string.isRequired,
  className: PropTypes.string,
  collapsed: PropTypes.bool,
  draggable: PropTypes.bool,
  Patient: PropTypes.shape({
    'First Name': PropTypes.string.isRequired,
    'Last Name': PropTypes.string.isRequired,
  }).isRequired,
  Prescriber: PropTypes.shape({
    'First Name': PropTypes.string,
    'Last Name': PropTypes.string,
  }),
  'Order Line': PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
    'Order ID': PropTypes.number,
  })).isRequired,
  Insurance: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })),
  Notes: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })),
  onStartDrag: PropTypes.func,
  onDrag: PropTypes.func,
  onStopDrag: PropTypes.func,
  variant: PropTypes.oneOf(['single-date', 'date-range']).isRequired,
  boundsSelector: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  hidden: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    fetchOrder: PropTypes.func.isRequired,
  }).isRequired,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  onOrderLinesToggle: PropTypes.func,
  notAllowed: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    rootCollapsed: PropTypes.string.isRequired,
    rootDragged: PropTypes.string.isRequired,
    rootHovered: PropTypes.string.isRequired,
    rootHidden: PropTypes.string.isRequired,
    paper: PropTypes.string.isRequired,
    paperVariantSingleDate: PropTypes.string.isRequired,
    paperVariantDateRange: PropTypes.string.isRequired,
    paperHovered: PropTypes.string.isRequired,
    paperDisabled: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    contentCollapsed: PropTypes.string.isRequired,
    iconMoreVert: PropTypes.string.isRequired,
    buttonEditPatientAddress: PropTypes.string.isRequired,
    buttonMoreMenu: PropTypes.string.isRequired,
    contentPrimary: PropTypes.string.isRequired,
    gridItemPatientName: PropTypes.string.isRequired,
    gridItemPatientNameNoAddress: PropTypes.string.isRequired,
    typographyPrimary: PropTypes.string.isRequired,
    typographySecondary: PropTypes.string.isRequired,
    contentSecondary: PropTypes.string.isRequired,
    gridItemDriverName: PropTypes.string.isRequired,
    orderLines: PropTypes.string.isRequired,
    scrollbarsOrderLineDragged: PropTypes.string.isRequired,
    trackVertical: PropTypes.string.isRequired,
    trackHorizontal: PropTypes.string.isRequired,
    collapseEntered: PropTypes.string.isRequired,
    iconCheck: PropTypes.string.isRequired,
  }).isRequired,
};

ScheduleCalendarItem.defaultProps = {
  collapsed: false,
  draggable: true,
  loading: false,
  disabled: false,
  hidden: false,
  className: '',
  onStartDrag: null,
  onDrag: null,
  onStopDrag: null,
  boundsSelector: null,
  Prescriber: null,
  business: {},
  Insurance: [],
  Notes: [],
  notAllowed: false,
  onOrderLinesToggle: () => {},
};

const mapStateToProps = store => ({
  account: store.account,
  appInfo: store.appInfo,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ fetchOrder }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withRouter(withStyles(styles)(ScheduleCalendarItem)),
);



// WEBPACK FOOTER //
// ./src/components/schedule/ScheduleCalendar/ScheduleCalendarItem.js